<script>
/* global axios */
import { useAlert } from 'dashboard/composables';

// v188 — além de foto+nome, a aba configura:
//  - Links no topo da página (Site/Instagram/Facebook), cada um com ativo+URL;
//  - Instalação PWA (tela "Instale o app…"), com mensagem/botões por plataforma
//    e quando exibir (ao iniciar OU botão "Ler Notificação!" no chat).
// TUDO nasce DESLIGADO (on:false / enabled:false) — o admin liga aqui depois.
const defaultLinks = () => ({
  site: { on: false, url: '' },
  instagram: { on: false, url: '' },
  facebook: { on: false, url: '' },
});
const defaultSoftAsk = () => ({
  title: 'Quer ser avisado quando ficar pronto?',
  text: 'Ativo um aviso no seu celular assim que eu tiver a resposta — mesmo com esta aba fechada.',
  btn_yes: 'Ativar avisos',
  btn_no: 'Agora não',
  snooze_days: 3,
});
const defaultChromeRedirect = () => ({
  on: false,
  msg: 'Neste navegador o aviso não funciona direito. Abro sua conversa no Chrome?',
  btn: 'Abrir no Chrome',
});
const defaultPush = () => ({
  enabled: false,
  prompt_when: 'after_first_reply',
  every_message: false,
  soft_ask: defaultSoftAsk(),
  chrome_redirect: defaultChromeRedirect(),
  labels: {},
});
// Mensagens sugeridas ao ligar as etiquetas principais do funil
const PUSH_SUGGESTIONS = {
  fgts_simulacao: 'Sua oferta de crédito está pronta! Toque para ver.',
  fgts_link: 'Seu link de formalização chegou! Toque para assinar.',
  fgts_pago: 'Seu contrato foi pago! ✅',
  clt_simulacao: 'Sua simulação do consignado CLT ficou pronta! Toque para ver.',
  clt_link: 'Seu link de formalização chegou! Toque para assinar.',
  clt_pago: 'Seu contrato foi pago! ✅',
  clt_retorno_agendado: 'Voltamos a falar do seu crédito — toque para continuar.',
};
const defaultPwa = () => ({
  enabled: false,
  when: 'start',
  android: {
    on: false,
    msg: 'Instale o app da Mazam e Simule seu crédito',
    btn_ok: 'Prosseguir',
    btn_alt: 'Continuar pelo Site',
  },
  ios: {
    on: false,
    msg: 'Instale o app da Mazam e Simule seu crédito',
    btn_ok: 'Prosseguir',
    btn_alt: 'Continuar pelo Site',
  },
});

export default {
  props: {
    inbox: { type: Object, required: true },
  },
  data() {
    return {
      name: '',
      photo: '',
      verified: false,
      inboxAvatar: '',
      links: defaultLinks(),
      pwa: defaultPwa(),
      push: defaultPush(),
      domain: '',
      theme: { brand: '' },
      brand: { logo: '', favicon: '', site_title: '' },
      start: { title: '', subtitle: '', consent: '' },
      saving: false,
      stats: null,
    };
  },
  computed: {
    accountId() {
      return this.$route.params.accountId;
    },
    vpsIp() {
      return '179.197.226.84';
    },
    cleanDomain() {
      return (this.domain || '')
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/\/.*$/, '')
        .replace(/^www\./, '');
    },
    previewSrc() {
      return this.photo || this.inboxAvatar || '';
    },
    // Todas as etiquetas da conta (para escolher quais disparam push)
    allLabels() {
      return this.$store.getters['labels/getLabels'] || [];
    },
  },
  watch: {
    allLabels() {
      this.ensureLabelRules();   // etiquetas chegam async do store
    },
  },
  mounted() {
    this.$store.dispatch('labels/get');   // garante o store de etiquetas carregado
    this.load();
    this.loadStats();
  },
  methods: {
    // Números do convite de notificação — sem eles, ajustar texto/momento é chute.
    async loadStats() {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/mazam_web/stats`,
          { params: { inbox_id: this.inbox.id, days: 30 } }
        );
        this.stats = data.resumo || null;
      } catch (e) {
        this.stats = null;
      }
    },
    async load() {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/mazam_web/config`,
          { params: { inbox_id: this.inbox.id } }
        );
        this.name = data.name || '';
        this.photo = data.photo || '';
        this.verified = data.verified === true;
        this.inboxAvatar = data.inbox_avatar || '';
        // merge sobre os defaults (config antiga sem os campos novos = tudo OFF)
        this.links = Object.assign(defaultLinks(), data.links || {});
        const pwa = Object.assign(defaultPwa(), data.pwa || {});
        pwa.android = Object.assign(defaultPwa().android, (data.pwa || {}).android || {});
        pwa.ios = Object.assign(defaultPwa().ios, (data.pwa || {}).ios || {});
        this.pwa = pwa;
        const push = Object.assign(defaultPush(), data.push || {});
        push.labels = Object.assign({}, (data.push || {}).labels || {});
        push.soft_ask = Object.assign(defaultSoftAsk(), (data.push || {}).soft_ask || {});
        push.chrome_redirect = Object.assign(
          defaultChromeRedirect(), (data.push || {}).chrome_redirect || {}
        );
        this.push = push;
        this.domain = data.domain || '';
        this.theme = Object.assign({ brand: '' }, data.theme || {});
        this.brand = Object.assign({ logo: '', favicon: '', site_title: '' }, data.brand || {});
        this.start = Object.assign({ title: '', subtitle: '', consent: '' }, data.start || {});
        this.ensureLabelRules();
      } catch (e) {
        // sem config ainda — começa vazio/desligado
      }
    },
    // Garante uma entrada {on,msg} para cada etiqueta da conta (reativo)
    ensureLabelRules() {
      (this.allLabels || []).forEach(l => {
        const t = l.title;
        if (!this.push.labels[t]) {
          this.push.labels[t] = { on: false, msg: PUSH_SUGGESTIONS[t] || '' };
        } else if (!this.push.labels[t].msg && PUSH_SUGGESTIONS[t]) {
          this.push.labels[t].msg = PUSH_SUGGESTIONS[t];
        }
      });
    },
    pickImage(field, maxKB, e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      if (file.size > maxKB * 1024) {
        useAlert(`Imagem muito grande (máx. ${maxKB} KB).`);
        e.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        this.brand[field] = reader.result;
      };
      reader.readAsDataURL(file);
    },
    clearImage(field) {
      this.brand[field] = '';
    },
    async save() {
      this.saving = true;
      // envia só as etiquetas marcadas (mantém o additional_attributes enxuto)
      const labels = {};
      Object.keys(this.push.labels || {}).forEach(t => {
        const r = this.push.labels[t];
        if (r && r.on) labels[t] = { on: true, msg: (r.msg || '').trim() };
      });
      const pushPayload = {
        enabled: this.push.enabled,
        prompt_when: this.push.prompt_when,
        every_message: this.push.every_message,
        soft_ask: this.push.soft_ask,
        chrome_redirect: this.push.chrome_redirect,
        labels,
      };
      try {
        await axios.post(
          `/api/v1/accounts/${this.accountId}/mazam_web/config`,
          {
            inbox_id: this.inbox.id,
            name: this.name,
            photo: this.photo,
            verified: this.verified,
            links: this.links,
            pwa: this.pwa,
            push: pushPayload,
            domain: this.cleanDomain,
            theme: this.theme,
            brand: this.brand,
            start: this.start,
          }
        );
        useAlert('Configuração do site salva.');
      } catch (e) {
        useAlert('Não foi possível salvar a configuração do site.');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<template>
  <form class="py-6" @submit.prevent="save">
    <p class="text-sm text-slate-500 dark:text-slate-400 mb-6">
      Define a foto e o nome que aparecem no topo da página do cliente
      (seu domínio). Não altera o nome interno da caixa de entrada.
    </p>

    <div class="mb-6">
      <label class="block mb-2 text-sm font-medium">Foto exibida no site</label>
      <div style="display:flex;align-items:center;gap:14px">
        <img
          v-if="previewSrc"
          :src="previewSrc"
          alt="prévia"
          style="width:56px;height:56px;border-radius:50%;object-fit:cover;flex:0 0 auto"
        />
        <div
          v-else
          style="width:56px;height:56px;border-radius:50%;background:#000;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:600;font-size:22px;flex:0 0 auto"
        >
          M
        </div>
        <input
          v-model="photo"
          type="text"
          style="flex:1"
          placeholder="URL da imagem (vazio = usa o avatar da caixa de entrada)"
        />
      </div>
    </div>

    <div class="mb-6">
      <label class="block mb-2 text-sm font-medium">Nome exibido no site</label>
      <div style="display:flex;align-items:center;gap:10px">
        <input v-model="name" type="text" class="!mb-0" style="flex:1" placeholder="Ex.: Mazam" />
        <svg v-if="verified" viewBox="0 0 22 22" style="width:22px;height:22px;flex:0 0 auto" aria-label="Selo">
          <path fill="#1d9bf0" d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.854-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.054-.878 1.687-.13.633-.08 1.29.144 1.897-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.634.433 1.218.877 1.688.47.443 1.054.747 1.688.878.633.132 1.29.084 1.897-.136.274.586.705 1.084 1.246 1.439.54.354 1.17.551 1.816.569.647-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.239 1.266.296 1.903.164.636-.132 1.22-.447 1.68-.907.46-.46.776-1.044.908-1.681s.075-1.299-.165-1.903c.586-.274 1.084-.705 1.439-1.246.354-.54.551-1.17.569-1.816z"/>
          <path fill="#fff" d="M9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z"/>
        </svg>
      </div>
      <label style="display:flex;align-items:center;gap:8px;cursor:pointer" class="mt-3">
        <input v-model="verified" type="checkbox" style="margin:0" />
        <span class="text-sm font-medium">Exibir selo de verificação</span>
      </label>
    </div>

    <div class="mb-6">
      <label class="block mb-2 text-sm font-medium">Domínio do site</label>
      <input v-model="domain" type="text" placeholder="Ex.: meuclt.com" />
      <div
        v-if="cleanDomain"
        class="mt-2 p-4 rounded-lg text-sm"
        style="background:rgba(29,155,240,.08);border:1px solid rgba(29,155,240,.35)"
      >
        <p class="font-semibold mb-2">📡 Apontamento no Cloudflare para {{ cleanDomain }}:</p>
        <ol class="list-decimal ml-5" style="line-height:1.9">
          <li>Registro <b>A</b> — nome <code>@</code> → conteúdo <code>{{ vpsIp }}</code></li>
          <li>Registro <b>CNAME</b> — nome <code>www</code> → conteúdo <code>{{ cleanDomain }}</code></li>
          <li>Nuvem <b>CINZA (DNS only)</b> até o site abrir com cadeado; depois pode ligar o proxy.</li>
        </ol>
      </div>
    </div>

    <div class="mb-6">
      <label class="block mb-2 text-sm font-medium">Cor principal do site</label>
      <div style="display:flex;align-items:center;gap:10px">
        <input v-model="theme.brand" type="color" style="width:52px;height:36px;padding:2px" />
        <input v-model="theme.brand" type="text" class="!mb-0" style="width:130px" placeholder="#008069" />
        <span class="text-xs opacity-70">Verde WhatsApp: #008069 · Azul: #1d9bf0 · Preto: #111111</span>
      </div>
    </div>

    <div class="mb-6">
      <label class="block mb-2 text-sm font-medium">Logomarca (ícones do app instalado — PNG/JPG até 300 KB)</label>
      <div style="display:flex;align-items:center;gap:12px">
        <img v-if="brand.logo" :src="brand.logo" alt="logo" style="width:44px;height:44px;border-radius:10px;object-fit:cover" />
        <input type="file" accept="image/png,image/jpeg" @change="e => pickImage('logo', 300, e)" />
        <button v-if="brand.logo" type="button" class="text-xs text-rose-400" @click="clearImage('logo')">remover</button>
      </div>
    </div>

    <div class="mb-6">
      <label class="block mb-2 text-sm font-medium">Favicon (ícone da aba — PNG até 100 KB)</label>
      <div style="display:flex;align-items:center;gap:12px">
        <img v-if="brand.favicon" :src="brand.favicon" alt="favicon" style="width:24px;height:24px" />
        <input type="file" accept="image/png" @change="e => pickImage('favicon', 100, e)" />
        <button v-if="brand.favicon" type="button" class="text-xs text-rose-400" @click="clearImage('favicon')">remover</button>
      </div>
    </div>

    <div class="mb-6">
      <label class="block mb-2 text-sm font-medium">Título da aba do navegador (ao lado do favicon)</label>
      <input v-model="brand.site_title" type="text" placeholder="Ex.: Meu CLT" />
    </div>

    <div class="mb-6">
      <label class="block mb-2 text-sm font-medium">Textos da tela inicial</label>
      <input v-model="start.title" type="text" class="mb-2" placeholder='Título — ex.: "Fale com a Meu CLT" (vazio = automático)' />
      <input v-model="start.subtitle" type="text" class="mb-2" placeholder='Subtítulo — ex.: "Informe seu número de WhatsApp para iniciar o atendimento."' />
      <input v-model="start.consent" type="text" placeholder='Consentimento — ex.: "Ao continuar, você concorda em ser contatado neste número."' />
    </div>

    <!-- ===== Links no topo da página ===== -->
    <hr class="my-6" style="border-color:#e2e8f0;opacity:.4" />
    <h3 class="text-base font-semibold mb-1">Links no topo da página</h3>
    <p class="text-sm text-slate-500 dark:text-slate-400 mb-4">
      Ícones clicáveis exibidos no cabeçalho do chat. Marque para ativar cada um.
    </p>

    <div
      v-for="(row, key) in links"
      :key="key"
      class="mb-3"
      style="display:flex;align-items:center;gap:12px"
    >
      <label style="display:flex;align-items:center;gap:8px;min-width:110px;cursor:pointer">
        <input v-model="row.on" type="checkbox" style="margin:0" />
        <span class="text-sm font-medium" style="text-transform:capitalize">{{ key === 'site' ? 'Site' : key === 'instagram' ? 'Instagram' : 'Facebook' }}</span>
      </label>
      <input
        v-model="row.url"
        type="text"
        style="flex:1"
        class="!mb-0"
        :placeholder="key === 'site' ? 'https://www.seusite.com.br' : key === 'instagram' ? 'https://instagram.com/perfil' : 'https://facebook.com/pagina'"
      />
    </div>

    <!-- ===== Instalação PWA ===== -->
    <hr class="my-6" style="border-color:#e2e8f0;opacity:.4" />
    <h3 class="text-base font-semibold mb-1">Instalação PWA (app na tela do celular)</h3>
    <p class="text-sm text-slate-500 dark:text-slate-400 mb-4">
      Mostra ao cliente uma tela convidando a instalar o app da Mazam.
      No Android a instalação é automática; no iPhone são exibidas as instruções.
    </p>

    <label style="display:flex;align-items:center;gap:8px;cursor:pointer" class="mb-4">
      <input v-model="pwa.enabled" type="checkbox" style="margin:0" />
      <span class="text-sm font-medium">Exibir Instalação PWA</span>
    </label>

    <div v-if="pwa.enabled" style="padding-left:4px">
      <div class="mb-5">
        <label class="block mb-2 text-sm font-medium">Quando exibir</label>
        <select v-model="pwa.when" style="max-width:420px">
          <option value="start">Exibir ao iniciar (logo após o cliente entrar)</option>
          <option value="chat">Exibir no chat (botão vermelho "Ler Notificação!")</option>
        </select>
      </div>

      <div
        v-for="plat in ['android', 'ios']"
        :key="plat"
        class="mb-5"
        style="border:1px solid rgba(148,163,184,.35);border-radius:10px;padding:14px 16px"
      >
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer" class="mb-3">
          <input v-model="pwa[plat].on" type="checkbox" style="margin:0" />
          <span class="text-sm font-semibold">{{ plat === 'android' ? 'Android' : 'iPhone' }}</span>
          <span class="text-xs text-slate-500">{{ pwa[plat].on ? 'ativo' : 'inativo' }}</span>
        </label>
        <div class="mb-2">
          <label class="block mb-1 text-xs font-medium">Mensagem</label>
          <input v-model="pwa[plat].msg" type="text" class="!mb-0" />
        </div>
        <div style="display:flex;gap:10px">
          <div style="flex:1">
            <label class="block mb-1 text-xs font-medium">Botão principal</label>
            <input v-model="pwa[plat].btn_ok" type="text" class="!mb-0" />
          </div>
          <div style="flex:1">
            <label class="block mb-1 text-xs font-medium">Botão secundário</label>
            <input v-model="pwa[plat].btn_alt" type="text" class="!mb-0" />
          </div>
        </div>
      </div>
    </div>

    <!-- ===== Notificações push ===== -->
    <hr class="my-6" style="border-color:#e2e8f0;opacity:.4" />
    <h3 class="text-base font-semibold mb-1">Notificações push (navegador)</h3>
    <p class="text-sm text-slate-500 dark:text-slate-400 mb-4">
      Avisa o cliente quando o atendente/IA responde, mesmo com a aba fechada.
      No iPhone só funciona se o cliente instalar o app (PWA). O aviso só aparece
      quando o cliente não está com o chat aberto.
    </p>

    <label style="display:flex;align-items:center;gap:8px;cursor:pointer" class="mb-4">
      <input v-model="push.enabled" type="checkbox" style="margin:0" />
      <span class="text-sm font-medium">Ativar notificações push</span>
    </label>

    <div v-if="push.enabled" style="padding-left:4px">
      <!-- Conversão real dos últimos 30 dias -->
      <div
        v-if="stats && stats.convites_exibidos"
        class="mb-5 p-4 rounded-lg"
        style="background:rgba(148,163,184,.07)"
      >
        <label class="block mb-3 text-sm font-medium">Últimos 30 dias</label>
        <div style="display:flex;flex-wrap:wrap;gap:18px 28px">
          <div>
            <div class="text-lg font-semibold">{{ stats.convites_exibidos }}</div>
            <div class="text-xs text-slate-500">convites exibidos</div>
          </div>
          <div>
            <div class="text-lg font-semibold">{{ stats.taxa_aceite_pct }}%</div>
            <div class="text-xs text-slate-500">aceitaram o convite</div>
          </div>
          <div>
            <div class="text-lg font-semibold">{{ stats.inscricoes }}</div>
            <div class="text-xs text-slate-500">receberão avisos</div>
          </div>
          <div>
            <div class="text-lg font-semibold">{{ stats.bloqueou_no_navegador }}</div>
            <div class="text-xs text-slate-500">bloquearam no navegador</div>
          </div>
          <div>
            <div class="text-lg font-semibold">{{ stats.suprimido_pelo_navegador }}</div>
            <div class="text-xs text-slate-500">o navegador silenciou</div>
          </div>
          <div>
            <div class="text-lg font-semibold">{{ stats.recuperacoes }}</div>
            <div class="text-xs text-slate-500">reativaram depois</div>
          </div>
          <div>
            <div class="text-lg font-semibold">{{ stats.apps_instalados }}</div>
            <div class="text-xs text-slate-500">instalaram o app</div>
          </div>
        </div>
        <p class="text-xs text-slate-500 mt-3">
          Uma taxa de aceite baixa faz o Chrome esconder o pedido no sininho da barra
          de endereço. Peça no momento certo e com texto claro para manter a taxa alta.
        </p>
      </div>

      <div class="mb-5">
        <label class="block mb-2 text-sm font-medium">Quando pedir a permissão</label>
        <select v-model="push.prompt_when" style="max-width:460px">
          <option value="after_first_reply">Após a 1ª resposta do atendente/IA (recomendado)</option>
          <option value="button">Botão "Ativar notificações" no chat</option>
          <option value="on_enter">Assim que o cliente entra no chat (pior conversão)</option>
        </select>
        <p class="text-xs text-slate-500 mt-1">
          O cliente vê primeiro um convite nosso (abaixo); o pedido oficial do navegador
          só aparece se ele aceitar — assim uma recusa não bloqueia o aviso para sempre.
          Pedir antes de entregar valor derruba o aceite: prefira "após a 1ª resposta"
          ou o pedido por etiqueta, no auge do interesse. No iPhone só funciona com o
          app instalado.
        </p>
      </div>

      <div class="mb-5 p-4 rounded-lg" style="background:rgba(148,163,184,.07)">
        <label class="block mb-2 text-sm font-medium">Convite exibido ao cliente</label>
        <input
          v-model="push.soft_ask.title"
          type="text"
          class="mb-2"
          placeholder="Título — ex.: Quer ser avisado quando ficar pronto?"
        />
        <input
          v-model="push.soft_ask.text"
          type="text"
          class="mb-2"
          placeholder="Texto — ex.: Aviso você no celular assim que tiver a resposta."
        />
        <div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap">
          <input v-model="push.soft_ask.btn_yes" type="text" class="!mb-0" style="max-width:190px" placeholder="Botão sim" />
          <input v-model="push.soft_ask.btn_no" type="text" class="!mb-0" style="max-width:190px" placeholder="Botão não" />
          <label style="display:flex;align-items:center;gap:6px" class="text-sm">
            Reoferecer após
            <input v-model.number="push.soft_ask.snooze_days" type="number" min="1" max="60" class="!mb-0" style="width:70px" />
            dias
          </label>
        </div>
        <p class="text-xs text-slate-500 mt-2">
          Se o cliente disser "agora não", só voltamos a perguntar depois desse prazo — ou
          antes, se ele chegar numa etiqueta marcada abaixo (momento de alta intenção).
        </p>
      </div>

      <label style="display:flex;align-items:center;gap:8px;cursor:pointer" class="mb-5">
        <input v-model="push.every_message" type="checkbox" style="margin:0" />
        <span class="text-sm font-medium">Notificar em toda resposta do atendente/IA</span>
      </label>

      <div class="mb-5 p-4 rounded-lg" style="background:rgba(148,163,184,.07)">
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer">
          <input v-model="push.chrome_redirect.on" type="checkbox" style="margin:0" />
          <span class="text-sm font-medium">Abrir no Chrome quando o navegador não suportar aviso</span>
        </label>
        <p class="text-xs text-slate-500 mt-1">
          No navegador embutido do WhatsApp/Instagram a notificação normalmente
          <b>nem existe</b>; no Samsung Internet, instalar o app dispara um alerta do
          Play Protect. Marcando aqui, o cliente do Android recebe um convite para
          continuar a conversa no Chrome (num toque, sem escolher navegador).
          Ele vai precisar informar o telefone de novo, porque é outro navegador.
          Só aparece no Android e não se repete por 7 dias.
        </p>
        <div v-if="push.chrome_redirect.on" class="mt-3">
          <input
            v-model="push.chrome_redirect.msg"
            type="text"
            class="mb-2"
            placeholder="Texto do convite"
          />
          <input
            v-model="push.chrome_redirect.btn"
            type="text"
            placeholder="Texto do botão — ex.: Abrir no Chrome"
            style="max-width:260px"
          />
        </div>
      </div>

      <label class="block mb-1 text-sm font-medium">Notificar quando estas etiquetas forem atribuídas</label>
      <p class="text-xs text-slate-500 mb-3">
        Marque a etiqueta e escreva a mensagem que o cliente recebe (ex.: oferta, link, pago).
      </p>
      <template v-for="l in allLabels" :key="l.id">
        <div
          v-if="push.labels[l.title]"
          class="mb-2"
          style="display:flex;align-items:center;gap:10px"
        >
          <label style="display:flex;align-items:center;gap:8px;min-width:190px;cursor:pointer">
            <input v-model="push.labels[l.title].on" type="checkbox" style="margin:0" />
            <span class="text-sm" style="word-break:break-all">{{ l.title }}</span>
          </label>
          <input
            v-if="push.labels[l.title].on"
            v-model="push.labels[l.title].msg"
            type="text"
            class="!mb-0"
            style="flex:1"
            placeholder="Mensagem da notificação"
          />
        </div>
      </template>
    </div>

    <hr class="my-6" style="border-color:#e2e8f0;opacity:.4" />

    <button
      type="submit"
      :disabled="saving"
      class="button"
      style="background:#12b886;color:#fff;border:none;padding:9px 18px;border-radius:8px;font-weight:600;cursor:pointer"
    >
      {{ saving ? 'Salvando…' : 'Atualizar' }}
    </button>
  </form>
</template>
